import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { readWalletCache, writeWalletCache, clearWalletCache } from "@/lib/walletCache";

const AuthCtx = createContext(null);

/**
 * AuthProvider — session bootstrapping + auth actions.
 *
 * Historical bug (fixed here): the `/auth/login` response's `user` object
 * omits wallet_* / bank_* fields — those only come from `/auth/me`. Before
 * this fix, freshly-logged-in parents landed on the dashboard with £0.00
 * until they hit F5 (which remounted AuthProvider and triggered `refresh()`).
 * The fix is to ALWAYS re-hydrate via `/auth/me` immediately after
 * setUser(...) on login OR register, and to persist the last-seen wallet
 * balance to sessionStorage so first paint is never zero.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, object=user

  // Merge sessionStorage-cached balance into a freshly-set user object so
  // the dashboard renders the last-known figures during the /auth/me window.
  const _applyCacheThenSet = (u) => {
    if (u && typeof u === "object") {
      const cached = readWalletCache();
      if (cached && cached.parent_id === (u.id || u.parent_id)) {
        // Only fill in fields that are missing from the login-response user
        // object — never overwrite a fresh value from the server.
        u = {
          ...u,
          wallet_available_pennies: u.wallet_available_pennies ?? cached.wallet_available_pennies,
          wallet_pending_pennies: u.wallet_pending_pennies ?? cached.wallet_pending_pennies,
          wallet_payout_requested_pennies: u.wallet_payout_requested_pennies ?? cached.wallet_payout_requested_pennies,
          bank_last4: u.bank_last4 || cached.bank_last4,
          bank_name: u.bank_name || cached.bank_name,
        };
      }
      writeWalletCache(u);
    }
    setUser(u);
  };

  const refresh = useCallback(async () => {
    // Iteration 96b — previously this short-circuited to `setUser(false)`
    // on any `/kidzone*` route to avoid a 401 in the console when only a
    // kid session was active. That broke the family-picker deep-link:
    // when a parent DID have a valid `mtt_session`, refreshing /kidzone
    // discarded it, leaving the roster empty. The /auth/me call below
    // already uses `validateStatus: (s) => s < 500` so a real 401 is
    // handled silently — we no longer need the path guard.
    try {
      const { data } = await api.get("/auth/me", { validateStatus: (s) => s < 500 });
      if (data && data.id) _applyCacheThenSet(data);
      else setUser(false);
    } catch { setUser(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Optimistically hydrate from the login response (fast first paint), then
    // immediately re-hydrate via /auth/me to fill in wallet_* / bank_* fields
    // that /auth/login doesn't return.
    _applyCacheThenSet(data.user);
    await refresh();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    _applyCacheThenSet(data.user);
    await refresh();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    clearWalletCache();
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, login, register, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
